import { SettingOutlined, ZoomInOutlined } from '@ant-design/icons';
import { gql } from '@apollo/client';
import { Badge, Button, Space } from 'antd';
import Page from 'components/Page';
import StatusTag from 'components/StatusTag';
import Table from 'components/Table';
import { useQuery } from 'hooks/apollo';
import useDataSource from 'hooks/useDataSource';
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LinkState, WebsiteCheckStatus, WebsiteCheckType } from 'shared/services/prisma/types';
import { uniqueArray } from 'shared/utils/unique';
import theme from 'shared/constants/theme.json';
import { hasLicenseLimit, reachedLicenseLimit } from 'shared/utils/license';
import { EDIT_MATOMO } from 'shared/constants/permissions';
import CheckWebsiteUrl from './CheckWebsiteUrl';

const GET_WEBSITE_CHECKS = gql`
  query GetWebsiteChecks($websiteId: ID!) {
    website(id: $websiteId) {
      id
      url
      checks {
        id
        type
        createdAt
        entryUrl
        status
        links {
          id
          url
          state
          status
          parent
        }
      }
    }
  }
`;

const columns = [{
  title: 'Date',
  dataIndex: 'createdAt',
  type: 'datetime',
  defaultSortOrder: 'descend',
}, {
  title: 'Type',
  dataIndex: 'type',
  render: type => (type === WebsiteCheckType.Scheduled ? 'Scheduled' : 'Manual'),
}, {
  title: 'Entry URL',
  dataIndex: 'entryUrl',
}, {
  title: 'Pages',
  dataIndex: 'urls',
  type: 'number',
}, {
  title: 'Links',
  dataIndex: 'links',
  type: 'number',
}, {
  title: 'Broken links',
  dataIndex: 'brokenLinks',
  type: 'number',
}, {
  title: 'Status',
  dataIndex: 'status',
  render: s => <StatusTag status={s} />,
  align: 'center',
}];

const licenseSetting = 'monthlyBrokenlinksCheckLimit';

const BrokenLinks = () => {
  const { dataSource, loading: dataSourceLoading, getDataSourceLicenseSetting, getDataSourceUsage, refetch: refetchDataSource, hasDataSourcePermission } = useDataSource({ includeLicenseUsage: true });
  const { data, loading: checksLoading, refetch, startPolling, stopPolling } = useQuery(GET_WEBSITE_CHECKS, { variables: { websiteId: dataSource?.website?.id }, skip: !dataSource?.website?.id });

  const [checkWebsiteUrl, setCheckWebsiteUrl] = useState(false);

  const canEdit = hasDataSourcePermission(EDIT_MATOMO);

  const tableData = data?.website?.checks.map(check => ({
    ...check,
    urls: uniqueArray(check.links, { prop: 'parent' }).length,
    links: check.links.length,
    brokenLinks: check.links.filter(x => x.state === LinkState.Broken).length,
  }));

  const shouldPoll = tableData?.some(x => [WebsiteCheckStatus.Pending, WebsiteCheckStatus.Running].includes(x.status));

  useEffect(() => {
    if (shouldPoll) {
      startPolling(1000);
    } else {
      stopPolling();
    }
  }, [shouldPoll]);

  const actions = useCallback((record) => [
    <Link key="results" to={`broken-links/${record.id}`}>
      <Button icon={<ZoomInOutlined />}>View</Button>
    </Link>,
  ], []);

  const onCheckCreated = () => {
    setCheckWebsiteUrl(false);
    refetch();
    refetchDataSource();
  };

  const limit = getDataSourceLicenseSetting(licenseSetting);
  const current = getDataSourceUsage(licenseSetting)?.current || 0;
  const reachedLimit = reachedLicenseLimit(current, limit);

  const extra = (
    <>
      {hasLicenseLimit(limit) && (
        <Space key="limit">
          <Badge style={{ backgroundColor: theme[(limit >= 0 && current >= limit) ? 'error-color' : 'success-color'] }} count={`${current || 0} / ${limit >= 0 ? limit : '∞'}`} showZero />
          Monthly checks used
        </Space>
      )}
      {canEdit && (
        <Link to={`/data-sources/${dataSource?.id}/analytics/settings/broken-links`}>
          <Button icon={<SettingOutlined />} key="setting">Settings</Button>
        </Link>
      )}
      <Button key="check" type="primary" onClick={() => setCheckWebsiteUrl(true)} disabled={reachedLimit}>Check page now</Button>
    </>
  );

  const loading = dataSourceLoading || checksLoading;

  return (
    <Page>
      <Page.Header title="Broken links" extra={extra} />
      <Page.Content>
        <Table dataSource={tableData} columns={columns} actions={actions} loading={loading} />
        <CheckWebsiteUrl website={data?.website} open={checkWebsiteUrl} onCancel={() => setCheckWebsiteUrl(false)} onDone={onCheckCreated} />
      </Page.Content>
    </Page>
  );
};

export default BrokenLinks;
